import { useState } from 'react';
import { Download, RefreshCw, FileText, DollarSign, Shield, Lock, Package, Activity, User, MessageSquare } from 'lucide-react';
import { supabase } from '../../lib/supabase';

// ──────────────────────────────────────────────────────────
// Types
// ──────────────────────────────────────────────────────────

interface ReportDef {
  id: string;
  label: string;
  description: string;
  query: () => Promise<object[]>;
}

interface ReportGroup {
  id: string;
  label: string;
  icon: React.ElementType;
  color: string;
  reports: ReportDef[];
}

// ──────────────────────────────────────────────────────────
// CSV export helper
// ──────────────────────────────────────────────────────────

function exportCsv(rows: object[], filename: string) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const lines = [
    headers.join(','),
    ...rows.map(row =>
      headers.map(h => {
        const val = (row as Record<string, unknown>)[h];
        const str = val == null ? '' : String(val);
        return str.includes(',') || str.includes('"') || str.includes('\n')
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      }).join(',')
    ),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ──────────────────────────────────────────────────────────
// Report query functions
// ──────────────────────────────────────────────────────────

async function qLgpdDsarLog() {
  const { data } = await supabase
    .from('dsar_requests')
    .select('id, user_id, request_type, status, created_at, completed_at')
    .order('created_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qLgpdConsentLog() {
  const { data } = await supabase
    .from('consent_log')
    .select('id, user_id, consent_type, action, created_at, context')
    .order('created_at', { ascending: false })
    .limit(1000);
  return data ?? [];
}

async function qLgpdRetention() {
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 5);
  const { data } = await supabase
    .from('profiles')
    .select('id, email, created_at, last_active_at, subscription_tier')
    .lt('last_active_at', cutoff.toISOString())
    .order('last_active_at', { ascending: true })
    .limit(500);
  return data ?? [];
}

async function qLgpdMinors() {
  const { data } = await supabase
    .from('age_validation_log')
    .select('id, user_id, birth_date, age, result, context, created_at')
    .order('created_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qLgpdDataBreachLog() {
  const { data } = await supabase
    .from('audit_log')
    .select('id, user_id, action, resource, ip_address, created_at')
    .eq('action', 'data_breach_report')
    .order('created_at', { ascending: false })
    .limit(200);
  return data ?? [];
}

async function qLgpdArt18Requests() {
  const { data } = await supabase
    .from('dsar_requests')
    .select('id, user_id, request_type, status, created_at, completed_at, response_days:completed_at')
    .in('request_type', ['access', 'deletion', 'portability', 'correction', 'objection'])
    .order('created_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qFinRevenue() {
  const { data } = await supabase
    .from('payments')
    .select('id, user_id, amount, currency, status, product_id, created_at, payment_method')
    .eq('status', 'paid')
    .order('created_at', { ascending: false })
    .limit(1000);
  return data ?? [];
}

async function qFinChargebacks() {
  const { data } = await supabase
    .from('payments')
    .select('id, user_id, amount, currency, status, created_at, chargeback_reason')
    .eq('status', 'chargeback')
    .order('created_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qFinRefunds() {
  const { data } = await supabase
    .from('payments')
    .select('id, user_id, amount, currency, status, refund_reason, created_at, refunded_at')
    .eq('status', 'refunded')
    .order('created_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qFinSubscriptions() {
  const { data } = await supabase
    .from('profiles')
    .select('id, subscription_tier, subscription_expires_at, created_at')
    .neq('subscription_tier', 'free')
    .order('created_at', { ascending: false })
    .limit(1000);
  return data ?? [];
}

async function qFinCoupons() {
  const { data } = await supabase
    .from('coupon_uses')
    .select('id, user_id, coupon_code, discount_brl, created_at')
    .order('created_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qPrivConsentMatrix() {
  const { data } = await supabase
    .from('profiles')
    .select('id, email, marketing_email_consent, platform_alerts_consent, marketing_email_consented_at, platform_alerts_consented_at, created_at')
    .order('created_at', { ascending: false })
    .limit(2000);
  return data ?? [];
}

async function qPrivRevocations() {
  const { data } = await supabase
    .from('consent_log')
    .select('id, user_id, consent_type, created_at, context')
    .eq('action', 'revoked')
    .order('created_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qPrivMarketingOptOut() {
  const { data } = await supabase
    .from('profiles')
    .select('id, email, marketing_email_consent, marketing_email_consented_at')
    .eq('marketing_email_consent', false)
    .order('marketing_email_consented_at', { ascending: false })
    .limit(1000);
  return data ?? [];
}

async function qPrivCookieLog() {
  const { data } = await supabase
    .from('cookie_consent_log')
    .select('id, session_id, user_id, analytics, marketing, essential, created_at')
    .order('created_at', { ascending: false })
    .limit(1000);
  return (data ?? []).length ? data! : [{ info: 'Tabela cookie_consent_log não encontrada ou vazia' }];
}

async function qPrivDeleteRequests() {
  const { data } = await supabase
    .from('dsar_requests')
    .select('id, user_id, status, created_at, completed_at')
    .eq('request_type', 'deletion')
    .order('created_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qSecAuditLog() {
  const { data } = await supabase
    .from('audit_log')
    .select('id, user_id, action, resource, ip_address, user_agent, created_at')
    .order('created_at', { ascending: false })
    .limit(1000);
  return data ?? [];
}

async function qSecFailedLogins() {
  const { data } = await supabase
    .from('audit_log')
    .select('id, user_id, action, ip_address, created_at, metadata')
    .eq('action', 'failed_login')
    .order('created_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qSec2fa() {
  const { data } = await supabase
    .from('profiles')
    .select('id, email, totp_enabled, created_at, subscription_tier')
    .order('created_at', { ascending: false })
    .limit(2000);
  return data ?? [];
}

async function qSecApiKeys() {
  const { data } = await supabase
    .from('audit_log')
    .select('id, user_id, action, resource, ip_address, created_at')
    .eq('action', 'api_key_rotated')
    .order('created_at', { ascending: false })
    .limit(200);
  return data ?? [];
}

async function qVendorEdgeFunctions() {
  const { data } = await supabase
    .from('edge_function_logs')
    .select('id, function_name, status, duration_ms, error, created_at')
    .order('created_at', { ascending: false })
    .limit(1000);
  return (data ?? []).length ? data! : [{ info: 'Tabela edge_function_logs não encontrada ou vazia' }];
}

async function qVendorApiUsage() {
  const { data } = await supabase
    .from('api_usage_log')
    .select('id, provider, model, tokens_used, cost_usd, created_at')
    .order('created_at', { ascending: false })
    .limit(1000);
  return (data ?? []).length ? data! : [{ info: 'Tabela api_usage_log não encontrada ou vazia' }];
}

async function qOpsReadingAnalytics() {
  const { data } = await supabase
    .from('reading_analytics')
    .select('id, user_id, feature, created_at')
    .order('created_at', { ascending: false })
    .limit(2000);
  return data ?? [];
}

async function qOpsActiveUsers() {
  const since = new Date();
  since.setDate(since.getDate() - 30);
  const { data } = await supabase
    .from('profiles')
    .select('id, subscription_tier, last_active_at, created_at')
    .gte('last_active_at', since.toISOString())
    .order('last_active_at', { ascending: false })
    .limit(2000);
  return data ?? [];
}

async function qOpsChurn() {
  const since = new Date();
  since.setDate(since.getDate() - 90);
  const { data } = await supabase
    .from('profiles')
    .select('id, subscription_tier, subscription_expires_at, last_active_at, created_at')
    .lt('subscription_expires_at', new Date().toISOString())
    .gte('subscription_expires_at', since.toISOString())
    .order('subscription_expires_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qOpsNewSignups() {
  const since = new Date();
  since.setDate(since.getDate() - 30);
  const { data } = await supabase
    .from('profiles')
    .select('id, full_name, subscription_tier, created_at, birth_city')
    .gte('created_at', since.toISOString())
    .order('created_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qOpsCreditUsage() {
  const { data } = await supabase
    .from('credit_transactions')
    .select('id, user_id, feature, amount, balance_after, created_at')
    .order('created_at', { ascending: false })
    .limit(1000);
  return (data ?? []).length ? data! : [{ info: 'Tabela credit_transactions não encontrada ou vazia' }];
}

async function qUserSelfService() {
  const { data } = await supabase
    .from('profile_change_history')
    .select('id, user_id, change_type, created_at, old_birth_date, new_birth_date')
    .order('created_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qUserFeedback() {
  const { data } = await supabase
    .from('reading_feedback')
    .select('id, user_id, feature, reading_id, rating, comment, created_at')
    .order('created_at', { ascending: false })
    .limit(500);
  return data ?? [];
}

async function qUserSupportTickets() {
  const { data } = await supabase
    .from('support_tickets')
    .select('id, user_id, subject, status, priority, created_at, resolved_at')
    .order('created_at', { ascending: false })
    .limit(500);
  return (data ?? []).length ? data! : [{ info: 'Tabela support_tickets não encontrada ou vazia' }];
}

async function qWhatsappDailyBriefing() {
  const now = new Date();
  const yesterday = new Date(now.getTime() - 86_400_000);

  const [{ data: newUsers }, { data: payments }, { data: readings }, { data: chargebacks }] = await Promise.all([
    supabase.from('profiles').select('id, subscription_tier').gte('created_at', yesterday.toISOString()),
    supabase.from('payments').select('id, amount, status').gte('created_at', yesterday.toISOString()),
    supabase.from('reading_analytics').select('feature').gte('created_at', yesterday.toISOString()),
    supabase.from('payments').select('id, amount').eq('status', 'chargeback').gte('created_at', yesterday.toISOString()),
  ]);

  const totalRevenue = (payments ?? []).filter(p => p.status === 'paid').reduce((s, p) => s + (p.amount ?? 0), 0);
  const featureCounts: Record<string, number> = {};
  for (const r of readings ?? []) featureCounts[r.feature] = (featureCounts[r.feature] ?? 0) + 1;
  const topFeature = Object.entries(featureCounts).sort((a, b) => b[1] - a[1])[0];

  return [{
    date: now.toLocaleDateString('pt-BR'),
    new_users: (newUsers ?? []).length,
    new_paid: (newUsers ?? []).filter(u => u.subscription_tier !== 'free').length,
    revenue_brl: `R$ ${(totalRevenue / 100).toFixed(2).replace('.', ',')}`,
    total_readings: (readings ?? []).length,
    top_feature: topFeature ? `${topFeature[0]} (${topFeature[1]}x)` : '-',
    chargebacks: (chargebacks ?? []).length,
  }];
}

// ──────────────────────────────────────────────────────────
// Report group definitions
// ──────────────────────────────────────────────────────────

const REPORT_GROUPS: ReportGroup[] = [
  {
    id: 'A', label: 'A, Legal (LGPD/GDPR/CDC)', icon: FileText, color: '#60a5fa',
    reports: [
      { id: 'A1', label: 'A1 · Log de DSARs (Art. 18 LGPD)', description: 'Solicitações de acesso, exclusão, portabilidade e correção de dados.', query: qLgpdArt18Requests },
      { id: 'A2', label: 'A2 · Log de Consentimentos', description: 'Histórico de concessões e revogações de consentimento por usuário.', query: qLgpdConsentLog },
      { id: 'A3', label: 'A3 · Retenção de Dados (> 5 anos)', description: 'Usuários com dados armazenados além do período de retenção.', query: qLgpdRetention },
      { id: 'A4', label: 'A4 · Validação de Menores', description: 'Registros de validação de idade, bloqueios e passagens.', query: qLgpdMinors },
      { id: 'A5', label: 'A5 · Notificação de Incidentes (Art. 48)', description: 'Registros de notificação de violação de dados.', query: qLgpdDataBreachLog },
      { id: 'A6', label: 'A6 · Exercício de Direitos (Art. 18)', description: 'Relatório completo de DSARs com tempo de resposta.', query: qLgpdDsarLog },
    ],
  },
  {
    id: 'B', label: 'B, Financeiro', icon: DollarSign, color: '#4ade80',
    reports: [
      { id: 'B1', label: 'B1 · Receita Confirmada', description: 'Todos os pagamentos com status "paid" por produto e período.', query: qFinRevenue },
      { id: 'B2', label: 'B2 · Chargebacks', description: 'Contestações de pagamento registradas com motivo.', query: qFinChargebacks },
      { id: 'B3', label: 'B3 · Reembolsos', description: 'Reembolsos concedidos com razão e data.', query: qFinRefunds },
      { id: 'B4', label: 'B4 · Assinaturas Ativas', description: 'Todos os usuários com plano pago e data de expiração.', query: qFinSubscriptions },
      { id: 'B5', label: 'B5 · Uso de Cupons', description: 'Histórico de uso de cupons de desconto com valor descontado.', query: qFinCoupons },
    ],
  },
  {
    id: 'C', label: 'C, Privacidade / Consentimento', icon: Lock, color: '#c084fc',
    reports: [
      { id: 'C1', label: 'C1 · Matriz de Consentimento', description: 'Status de consentimento por usuário (marketing, alertas).', query: qPrivConsentMatrix },
      { id: 'C2', label: 'C2 · Revogações de Consentimento', description: 'Log de todos os opt-outs realizados.', query: qPrivRevocations },
      { id: 'C3', label: 'C3 · Opt-out de Marketing', description: 'Usuários que removeram consentimento de e-mails de marketing.', query: qPrivMarketingOptOut },
      { id: 'C4', label: 'C4 · Log de Cookies', description: 'Registros de consentimento de cookies por sessão.', query: qPrivCookieLog },
      { id: 'C5', label: 'C5 · Pedidos de Exclusão', description: 'DSARs de exclusão com status e prazo de resposta.', query: qPrivDeleteRequests },
    ],
  },
  {
    id: 'D', label: 'D, Segurança', icon: Shield, color: '#fb7185',
    reports: [
      { id: 'D1', label: 'D1 · Log de Auditoria Completo', description: 'Todas as ações registradas no audit_log.', query: qSecAuditLog },
      { id: 'D2', label: 'D2 · Tentativas de Login Falhadas', description: 'Logins falhos com IP e user agent.', query: qSecFailedLogins },
      { id: 'D3', label: 'D3 · Adoção de 2FA', description: 'Usuários com e sem autenticação de dois fatores.', query: qSec2fa },
      { id: 'D4', label: 'D4 · Rotação de Chaves de API', description: 'Histórico de rotação de API keys registradas no audit.', query: qSecApiKeys },
    ],
  },
  {
    id: 'E', label: 'E, Fornecedores', icon: Package, color: '#f59e0b',
    reports: [
      { id: 'E1', label: 'E1 · Performance de Edge Functions', description: 'Latência, erros e volume por função de borda.', query: qVendorEdgeFunctions },
      { id: 'E2', label: 'E2 · Uso de APIs de IA', description: 'Tokens consumidos e custo por provedor e modelo.', query: qVendorApiUsage },
    ],
  },
  {
    id: 'F', label: 'F, Operacional', icon: Activity, color: '#34d399',
    reports: [
      { id: 'F1', label: 'F1 · Analytics de Leituras', description: 'Uso de cada feature por usuário e data.', query: qOpsReadingAnalytics },
      { id: 'F2', label: 'F2 · Usuários Ativos (30 dias)', description: 'Todos os usuários com atividade nos últimos 30 dias.', query: qOpsActiveUsers },
      { id: 'F3', label: 'F3 · Churn (últimos 90 dias)', description: 'Assinaturas expiradas nos últimos 90 dias.', query: qOpsChurn },
      { id: 'F4', label: 'F4 · Novos Cadastros (30 dias)', description: 'Novos usuários registrados no último mês.', query: qOpsNewSignups },
      { id: 'F5', label: 'F5 · Uso de Créditos', description: 'Transações de crédito por feature e usuário.', query: qOpsCreditUsage },
    ],
  },
  {
    id: 'G', label: 'G, Usuário Final', icon: User, color: '#a78bfa',
    reports: [
      { id: 'G1', label: 'G1 · Alterações de Dados Cadastrais', description: 'Histórico de mudanças de dados de nascimento e nome.', query: qUserSelfService },
      { id: 'G2', label: 'G2 · Feedback de Leituras', description: 'Avaliações e comentários em leituras geradas.', query: qUserFeedback },
      { id: 'G3', label: 'G3 · Tickets de Suporte', description: 'Todos os tickets com status e tempo de resolução.', query: qUserSupportTickets },
    ],
  },
  {
    id: 'H', label: 'H, Briefing Diário WhatsApp', icon: MessageSquare, color: '#fbbf24',
    reports: [
      { id: 'H1', label: 'H1 · Briefing Operacional do Dia', description: 'Resumo executivo: novos usuários, receita, leituras, chargebacks.', query: qWhatsappDailyBriefing },
    ],
  },
];

// ──────────────────────────────────────────────────────────
// Components
// ──────────────────────────────────────────────────────────

interface ReportCardProps {
  report: ReportDef;
  groupColor: string;
}

function ReportCard({ report, groupColor }: ReportCardProps) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<object[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  async function run() {
    setLoading(true);
    setError(null);
    try {
      const data = await report.query();
      setRows(data);
      setExpanded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao gerar relatório.');
    } finally {
      setLoading(false);
    }
  }

  const headers = rows?.length ? Object.keys(rows[0]) : [];

  return (
    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-card)', overflow: 'hidden', marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '0.875rem 1rem', gap: '0.75rem', background: 'var(--bg-surface)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.2rem', fontSize: 'var(--text-label)' }}>
            <span style={{ color: groupColor }}>{report.id}</span> &nbsp;{report.label.split(' · ')[1] ?? report.label}
          </p>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>{report.description}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', flexShrink: 0 }}>
          {rows && (
            <button
              onClick={() => exportCsv(rows, `${report.id}-${Date.now()}.csv`)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.7rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-subtle)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--text-caption)' }}
              title="Exportar CSV"
            >
              <Download size={13} /> CSV
            </button>
          )}
          <button
            onClick={run}
            disabled={loading}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.7rem', borderRadius: 'var(--radius-sm)', border: `1px solid ${groupColor}40`, background: `${groupColor}10`, color: groupColor, cursor: loading ? 'not-allowed' : 'pointer', fontSize: 'var(--text-caption)', opacity: loading ? 0.7 : 1 }}
          >
            <RefreshCw size={13} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            {loading ? 'Gerando...' : rows ? 'Atualizar' : 'Gerar'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '0.75rem 1rem', background: 'rgba(251,113,133,0.07)', borderTop: '1px solid rgba(251,113,133,0.2)' }}>
          <p style={{ color: '#fb7185', fontSize: 'var(--text-caption)', margin: 0 }}>{error}</p>
        </div>
      )}

      {rows && (
        <div>
          <button
            onClick={() => setExpanded(v => !v)}
            style={{ width: '100%', textAlign: 'left', background: 'rgba(0,0,0,0.15)', border: 'none', borderTop: '1px solid var(--border-subtle)', padding: '0.4rem 1rem', cursor: 'pointer', fontSize: 'var(--text-caption)', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}
          >
            <span>{rows.length} registro{rows.length !== 1 ? 's' : ''}</span>
            <span>{expanded ? '▲ Ocultar' : '▼ Mostrar'}</span>
          </button>

          {expanded && (
            <div style={{ overflowX: 'auto', maxHeight: '360px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontFamily: 'monospace' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-base)', position: 'sticky', top: 0 }}>
                    {headers.map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '0.4rem 0.75rem', color: 'var(--text-muted)', fontWeight: 600, borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap' }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 200).map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)', background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)' }}>
                      {headers.map(h => {
                        const val = (row as Record<string, unknown>)[h];
                        return (
                          <td key={h} style={{ padding: '0.3rem 0.75rem', color: 'var(--text-secondary)', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {val == null ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>null</span> : String(val)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 200 && (
                <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-caption)', padding: '0.5rem', margin: 0 }}>
                  Mostrando 200 de {rows.length} registros. Exporte o CSV para ver todos.
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────

export function AdminReportsPage() {
  const [activeGroup, setActiveGroup] = useState('A');
  const group = REPORT_GROUPS.find(g => g.id === activeGroup) ?? REPORT_GROUPS[0];

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1100px' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.375rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 0.25rem', fontFamily: 'var(--font-display)' }}>
          Relatórios
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', margin: 0 }}>
          31 relatórios em 8 grupos, Legal, Financeiro, Privacidade, Segurança, Fornecedores, Operacional, Usuário e Briefing.
        </p>
      </div>

      {/* Group tabs */}
      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
        {REPORT_GROUPS.map(g => {
          const Icon = g.icon;
          const active = g.id === activeGroup;
          return (
            <button
              key={g.id}
              onClick={() => setActiveGroup(g.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.35rem',
                padding: '0.4rem 0.75rem', borderRadius: 'var(--radius-sm)',
                border: `1px solid ${active ? g.color : 'var(--border-subtle)'}`,
                background: active ? `${g.color}15` : 'transparent',
                color: active ? g.color : 'var(--text-muted)',
                cursor: 'pointer', fontSize: 'var(--text-caption)', fontWeight: active ? 600 : 400,
                transition: 'all 150ms',
              }}
            >
              <Icon size={13} />
              {g.id}
            </button>
          );
        })}
      </div>

      {/* Group header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1rem', padding: '0.75rem 1rem', borderRadius: 'var(--radius-card)', border: `1px solid ${group.color}30`, background: `${group.color}08` }}>
        {(() => { const Icon = group.icon; return <Icon size={18} style={{ color: group.color }} />; })()}
        <p style={{ fontWeight: 700, color: group.color, margin: 0, fontSize: 'var(--text-label)' }}>{group.label}</p>
        <span style={{ marginLeft: 'auto', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{group.reports.length} relatório{group.reports.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Reports */}
      {group.reports.map(report => (
        <ReportCard key={report.id} report={report} groupColor={group.color} />
      ))}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
